/* eslint-disable max-lines */
// External dependencies
import React, { memo, useCallback, useContext, useEffect, useMemo, useState } from "react";

// Internal dependencies
import { isEmpty, TEAM_LINE, getCloudinaryUrl } from "@swiggy-private/js-utils";
import { SwiggyHeadersContext, GenericErrorContext } from "@swiggy-private/portal-swiggy-one-subscription-context/dist";
import { GenericError } from "@swiggy-private/portal-swiggy-one-subscription-ui/dist/Error";
import Header from "@swiggy-private/portal-swiggy-one-subscription-ui/dist/Header";
import { MAST_HEAD_DEFAULT_IMG_CLOUDINARY_ID } from "@swiggy-private/portal-swiggy-one-subscription-ui/dist/Home";
import { isVersionCodeAllowed } from "@swiggy-private/portal-swiggy-one-subscription-ui/dist/utils";

import type { NextPage } from "next";
import Head from "next/head";
import { useRouter } from "next/router";

// components
import SuccessContentHomePage from "../components/SuccessContentHomePage";

// constant
import { HOME_PAGE_HEADER_SCROLL_OFFSET } from "../constants";
import { APP_UPDATE } from "../constants/texts";

// Hooks
import useApplyCouponOnClick from "../hooks/useApplyCouponOnClick";
import useGetHomePageData from "../hooks/useGetHomePageData";
import { useWindowScrollPositions } from "../hooks/useWindowScrollPosition";

// Styles
import { Container } from "../styles/home";

// Utilities
import { getBridgeSdk, normalCloseWebView } from "../utils/bridgeSdk";

const HomePage: NextPage = () => {
    useGetHomePageData();
    const router = useRouter();
    const [couponCodeParams, setCouponCodeParams] = useState<string>("");
    const [showTopHeader, setShowTopHeader] = useState<boolean>(false);
    const { swiggyHeaders } = useContext(SwiggyHeadersContext);
    const { applyCouponOnClick, showBottomSheet, inputBoxError, setInputBoxError } = useApplyCouponOnClick();
    const { genericError } = useContext(GenericErrorContext);
    const scrollPositions = useWindowScrollPositions();
    const sdk = useMemo(() => getBridgeSdk(), []);

    useEffect(() => {
        if (scrollPositions.scrollY >= HOME_PAGE_HEADER_SCROLL_OFFSET && !showTopHeader) {
            setShowTopHeader(true);
        } else if (scrollPositions.scrollY < HOME_PAGE_HEADER_SCROLL_OFFSET && showTopHeader) {
            setShowTopHeader(false);
        }
    }, [scrollPositions, showTopHeader]);

    useEffect(() => {
        if (router.isReady) {
            history.pushState(null, "", "/swiggy-one-subscription/home/");
            setCouponCodeParams(router.query.coupon_code?.toString() || "");
        }
    }, [router.isReady, router.query.coupon_code]);

    const onBackButtonClick = useCallback(() => {
        normalCloseWebView();
    }, []);

    useEffect(() => {
        window.addEventListener("popstate", onBackButtonClick);

        return (): void => {
            window.removeEventListener("popstate", onBackButtonClick);
        };
    }, [onBackButtonClick]);

    useEffect(() => {
        sdk.changeStatusBarColor({
            style: "lightContent",
            statusBarMode: "lightContent",
            bottomBarMode: "default",
        }).catch(() => void 0);
    }, [sdk]);

    const GenericErrorPage = useCallback(() => {
        return (
            <>
                <Header onBackButtonClick={onBackButtonClick} isBackgroundWhite={true} applyTopPadding={true} />
                <GenericError />
            </>
        );
    }, [onBackButtonClick]);

    const AppVersionCodePage = useCallback(() => {
        return (
            <>
                <Header onBackButtonClick={onBackButtonClick} isBackgroundWhite={true} applyTopPadding={true} />
                <GenericError title={APP_UPDATE.TITLE} subtitle={APP_UPDATE.SUB_TITLE} showRetry={false} />
            </>
        );
    }, [onBackButtonClick]);

    const preloadMastHeaderImageURL = useMemo(() => {
        return getCloudinaryUrl(MAST_HEAD_DEFAULT_IMG_CLOUDINARY_ID, { width: 600 }, { teamLine: TEAM_LINE.PORTAL });
    }, []);

    const renderContent = useMemo(() => {
        if (!isEmpty(swiggyHeaders) && !isVersionCodeAllowed(swiggyHeaders)) {
            return <AppVersionCodePage />;
        }

        if (genericError) {
            return <GenericErrorPage />;
        }

        return (
            <>
                {showTopHeader && (
                    <Header onBackButtonClick={onBackButtonClick} isBackgroundWhite={true} isFixed={true} />
                )}
                <SuccessContentHomePage
                    onBackButtonClick={onBackButtonClick}
                    couponCodeParams={couponCodeParams}
                    applyCouponOnClick={applyCouponOnClick}
                    inputBoxError={inputBoxError}
                    setInputBoxError={setInputBoxError}
                    showBottomSheet={showBottomSheet}
                />
            </>
        );
    }, [
        swiggyHeaders,
        genericError,
        AppVersionCodePage,
        GenericErrorPage,
        applyCouponOnClick,
        onBackButtonClick,
        showTopHeader,
        couponCodeParams,
        inputBoxError,
        setInputBoxError,
        showBottomSheet,
    ]);

    return (
        <>
            <Head>
                <title>Welcome to Home</title>
                <meta name="description" content="Welcome to Home" />
                <meta
                    name="viewport"
                    content="width=device-width,initial-scale=1,maximum-scale=1,user-scalable=no,viewport-fit=cover"
                />
                <link rel="icon" href="data:;base64,iVBORw0KGgo=" />
                <link rel="preconnect" href={process.env.NEXT_PUBLIC_CDN_URL} crossOrigin="anonymous" />
                <link rel="dns-prefetch" href={process.env.NEXT_PUBLIC_CDN_URL} />
                <link rel="preload" href={preloadMastHeaderImageURL} as="image" />
            </Head>
            <Container>{renderContent}</Container>
        </>
    );
};

export default memo(HomePage);
